// ─────────────────────────────────────────────────────────────────
// Glass-i — Goods Receipt Notes (GRN) LIST page (Stage 6P.3).
//
// Server-paginated list at /erp/purchase/grns.
//
// Columns (brief Section M):
//   GRN No (link)  · GRN Date · PO No (link to /erp/purchase/orders/:poId)
//   Supplier (+ GSTIN tag) · Supplier Invoice No · Status (Tag via
//   statusColor) · Received By · Line Count · Actions (⋯ menu).
//
// Toolbar:
//   Search (q) · Status filter · Date range · "New GRN" CTA ·
//   secondary "Create from PO" CTA — opens a small modal that asks
//   for a PO No / PO id and navigates to /erp/purchase/grns/from-po/:poId.
//
// Pagination: 25 / 50 / 100.
//
// ADDITIVE-ONLY: this page reads `goodsReceiptsApi` + the shared
// status / capability helpers (`statusLabel`, `statusColor`,
// `canEditGrn`, `canCancelGrn`, `canDeleteGrn`, `GRN_STATUS_OPTIONS`)
// from `./grnHelpers`. The GRN detail / create / edit / from-po
// routes are owned by the GRN editor page — this list page only
// navigates and never mutates rows itself except for the cancel
// and soft-delete endpoints shipped by the 6P.3 backend.
//
// Mirrors compactness from PurchaseOrdersPage:
//   • slate-50 header strip + uppercase column titles
//   • 22 px row density
//   • sticky toolbar
//   • ⋯ row-actions menu
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, DatePicker, Descriptions, Dropdown, Form, Input,
  InputNumber, Modal, Space, Spin, Table, Tooltip, message
} from 'antd';
import {
  PlusOutlined, ReloadOutlined, SearchOutlined, MoreOutlined,
  InboxOutlined, ExclamationCircleOutlined, ImportOutlined,
  EyeOutlined, EditOutlined, DeleteOutlined, StopOutlined,
  PrinterOutlined
} from '@ant-design/icons';

import { goodsReceiptsApi } from './grnApi';
import { purchaseOrdersApi } from './purchaseApi';
import {
  GRN_STATUS_OPTIONS,
  statusLabel,
  statusDotColor,
  canEditGrn,
  canCancelGrn,
  canDeleteGrn
} from './grnHelpers';
import useAuth from '../../../hooks/useAuth';
import './GoodsReceiptsPage.css';

const { RangePicker } = DatePicker;

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = ['25', '50', '100'];
const DATE_FMT          = 'DD-MMM-YYYY';
const ISO_FMT           = 'YYYY-MM-DD';
const SEARCH_DEBOUNCE_MS = 250;

function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const day   = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year  = d.getFullYear();
  return `${day}-${month}-${year}`;
}

function pickField(row, snake, camel) {
  if (!row) return undefined;
  if (camel in row && row[camel] !== undefined && row[camel] !== null) return row[camel];
  if (snake in row && row[snake] !== undefined && row[snake] !== null) return row[snake];
  return undefined;
}

function errMessage(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

// "Received By" — backend may send a denormalised display name
// alongside the user id. Probe a small list of likely keys without
// inventing a new shape.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
function readReceivedBy(row) {
  if (!row || typeof row !== 'object') return '';
  const candidates = [
    row.received_by_name, row.receivedByName,
    row.received_by_username, row.receivedByUsername,
    row.received_by_display_name, row.receivedByDisplayName,
    row.received_by, row.receivedBy
  ];
  for (const c of candidates) {
    if (c !== undefined && c !== null && String(c).trim() !== '') {
      const s = String(c).trim();
      // Never surface a raw user UUID — only show a human-readable name.
      if (UUID_RE.test(s)) return '';
      return s;
    }
  }
  return '';
}

// ─────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────

export default function GoodsReceiptsPage() {
  const navigate = useNavigate();
  const { me } = useAuth();

  // Wildcard '*' is the master role; explicit `erp.purchase.grns.*`
  // strings cover admin/manager/staff/viewer per userPermissions.js.
  const hasPerm = useMemo(() => {
    const list = Array.isArray(me?.permissions) ? me.permissions : [];
    const set = new Set(list);
    const wildcard = set.has('*');
    return (key) => wildcard || set.has(key);
  }, [me]);

  const canView   = hasPerm('erp.purchase.grns.view');
  const canCreate = hasPerm('erp.purchase.grns.create');
  const canUpdate = hasPerm('erp.purchase.grns.update');
  const canRemove = hasPerm('erp.purchase.grns.delete');
  const canCancel = hasPerm('erp.purchase.grns.cancel');

  // ── Toolbar state ────────────────────────────────────────────
  const [searchInput, setSearchInput] = useState('');
  const [search,      setSearch]      = useState('');
  const [status,      setStatus]      = useState(null);
  const [dateRange,   setDateRange]   = useState(null);

  // ── Server pagination state ──────────────────────────────────
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total,    setTotal]    = useState(0);

  // ── Data state ───────────────────────────────────────────────
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId,  setBusyId]  = useState(null);

  // ── "Create from PO" modal ───────────────────────────────────
  const [fromPoOpen, setFromPoOpen] = useState(false);
  const [fromPoForm] = Form.useForm();

  // ── Debounced search wiring ──────────────────────────────────
  // Keep the visible input snappy, but only re-fetch once the
  // operator pauses typing — same pattern as the customer master.
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch((prev) => {
        if (prev === searchInput) return prev;
        setPage(1);
        return searchInput;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  // ── Load ─────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const params = { page, pageSize };
      const needle = (search || '').trim();
      // Send under BOTH `q` (brief language) and `search` (purchase
      // module convention) — backend tolerates the unused one via
      // stripUnknown:true on its Joi validator.
      if (needle) {
        params.q      = needle;
        params.search = needle;
      }
      if (status) params.status = status;
      if (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
        params.fromDate = dateRange[0].format
          ? dateRange[0].format(ISO_FMT)
          : new Date(dateRange[0]).toISOString().slice(0, 10);
        params.toDate   = dateRange[1].format
          ? dateRange[1].format(ISO_FMT)
          : new Date(dateRange[1]).toISOString().slice(0, 10);
      }

      const out = await goodsReceiptsApi.list(params);
      const list = Array.isArray(out) ? out
        : Array.isArray(out?.rows) ? out.rows
        : Array.isArray(out?.data) ? out.data
        : [];
      const count = Array.isArray(out) ? list.length
        : Number(out?.total ?? out?.count ?? list.length) || 0;

      setRows(list);
      setTotal(count);
    } catch (e) {
      message.error({
        content: errMessage(e, 'Failed to load goods receipts.'),
        key: 'grn-list'
      });
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [canView, page, pageSize, search, status, dateRange]);

  useEffect(() => { load(); }, [load]);

  // ── Toolbar handlers — any filter change resets page to 1 ────
  const onStatus = (v) => { setStatus(v || null); setPage(1); };
  const onDates  = (v) => { setDateRange(v || null); setPage(1); };
  const onReset  = () => {
    setSearchInput('');
    setSearch('');
    setStatus(null);
    setDateRange(null);
    setPage(1);
  };

  // ── Row navigation ───────────────────────────────────────────
  const openView = useCallback((row) => {
    const id = pickField(row, 'id', 'id');
    if (!id) return;
    navigate(`/erp/purchase/grns/${encodeURIComponent(id)}`);
  }, [navigate]);

  const openEdit = useCallback((row) => {
    const id = pickField(row, 'id', 'id');
    if (!id) return;
    navigate(`/erp/purchase/grns/${encodeURIComponent(id)}?mode=edit`);
  }, [navigate]);

  const openSourcePo = useCallback((row) => {
    const poId = pickField(row, 'po_id', 'poId');
    if (!poId) return;
    navigate(`/erp/purchase/orders/${encodeURIComponent(poId)}`);
  }, [navigate]);

  // Open the printable GRN Receipt in the in-app viewer.
  const openReport = useCallback((row) => {
    const id = pickField(row, 'id', 'id');
    if (!id) return;
    navigate(`/erp/purchase/grns/${encodeURIComponent(id)}/report`);
  }, [navigate]);

  const openNew = useCallback(() => {
    navigate('/erp/purchase/grns/new');
  }, [navigate]);

  // ── "Create from PO" modal handlers ──────────────────────────
  const openFromPoModal = useCallback(() => {
    fromPoForm.resetFields();
    setFromPoOpen(true);
  }, [fromPoForm]);

  const closeFromPoModal = useCallback(() => {
    setFromPoOpen(false);
  }, []);

  const submitFromPo = useCallback(async () => {
    try {
      const values = await fromPoForm.validateFields();
      const raw = String(values.poRef || '').trim();
      if (!raw) return;
      // The from-po route + backend (GET /orders/:id, POST /from-po/:poId,
      // GET /po-balance/:poId) all require a PO **UUID**. The operator may
      // type either a PO No (e.g. "PO-20260626-000001") or a UUID, so a
      // non-UUID MUST be resolved to its id here — handing the raw PO No to
      // the editor makes every downstream call fail with "id must be a
      // valid UUID".
      const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (UUID_RE.test(raw)) {
        setFromPoOpen(false);
        navigate(`/erp/purchase/grns/from-po/${encodeURIComponent(raw)}`);
        return;
      }
      // Non-UUID — resolve the PO No to its id via the PO list (q matches
      // po_no ILIKE on the server). Require an exact po_no match so a
      // partial / ambiguous entry never silently opens the wrong PO.
      try {
        const res = await purchaseOrdersApi.list({ q: raw, limit: 20 });
        const list = Array.isArray(res)
          ? res
          : (Array.isArray(res?.rows) ? res.rows
            : (Array.isArray(res?.data) ? res.data : []));
        const wanted = raw.toUpperCase();
        const match = list.find(
          (r) => String(r.po_no || r.poNo || '').trim().toUpperCase() === wanted);
        const poId = match && (match.id || match.po_id);
        if (!poId) {
          message.error(`No purchase order found for "${raw}".`);
          return;
        }
        setFromPoOpen(false);
        navigate(`/erp/purchase/grns/from-po/${encodeURIComponent(poId)}`);
      } catch (e) {
        message.error(errMessage(e, 'Could not locate that purchase order.'));
      }
    } catch {
      // validateFields already surfaced the field-level error.
    }
  }, [fromPoForm, navigate]);

  // ── Row actions — guarded by perms + status capability gates ─
  const runCancel = useCallback((row) => {
    const id = pickField(row, 'id', 'id');
    if (!id) return;
    let reason = '';
    Modal.confirm({
      title: 'Cancel goods receipt?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p style={{ marginBottom: 8, fontSize: 12.5 }}>
            This will mark GRN{' '}
            <strong>{pickField(row, 'grn_no', 'grnNo')}</strong>{' '}
            as cancelled. Provide a short reason (optional).
          </p>
          <Input.TextArea
            rows={3}
            maxLength={500}
            placeholder="Reason (optional)"
            onChange={(e) => { reason = e.target.value; }}
          />
        </div>
      ),
      okText: 'Cancel GRN',
      okButtonProps: { danger: true },
      cancelText: 'Keep',
      async onOk() {
        setBusyId(id);
        try {
          await goodsReceiptsApi.cancel(id, { reason: reason || undefined });
          message.success('GRN cancelled.');
          await load();
        } catch (e) {
          message.error(errMessage(e, 'Failed to cancel GRN.'));
          throw e;
        } finally {
          setBusyId(null);
        }
      }
    });
  }, [load]);

  const runDelete = useCallback((row) => {
    const id = pickField(row, 'id', 'id');
    if (!id) return;
    Modal.confirm({
      title: 'Delete goods receipt?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <span style={{ fontSize: 12.5 }}>
          This will soft-delete GRN{' '}
          <strong>{pickField(row, 'grn_no', 'grnNo')}</strong>.
          Only Draft GRNs can be deleted.
        </span>
      ),
      okText: 'Delete',
      okButtonProps: { danger: true },
      cancelText: 'Keep',
      async onOk() {
        setBusyId(id);
        try {
          await goodsReceiptsApi.remove(id);
          message.success('GRN deleted.');
          await load();
        } catch (e) {
          message.error(errMessage(e, 'Failed to delete GRN.'));
          throw e;
        } finally {
          setBusyId(null);
        }
      }
    });
  }, [load]);

  // ── Row-action menu builder ──────────────────────────────────
  const buildRowMenu = useCallback((row) => {
    const items = [];
    items.push({
      key:   'view',
      icon:  <EyeOutlined />,
      label: 'View'
    });
    items.push({
      key:   'print',
      icon:  <PrinterOutlined />,
      label: 'Print receipt'
    });
    if (canUpdate && canEditGrn(row)) {
      items.push({
        key:   'edit',
        icon:  <EditOutlined />,
        label: 'Edit'
      });
    }
    if (canCancel && canCancelGrn(row)) {
      items.push({
        key:   'cancel',
        icon:  <StopOutlined />,
        label: 'Cancel',
        danger: true
      });
    }
    if (canRemove && canDeleteGrn(row)) {
      if (items.length > 1) items.push({ type: 'divider' });
      items.push({
        key:   'delete',
        icon:  <DeleteOutlined />,
        label: 'Delete',
        danger: true
      });
    }
    return items;
  }, [canUpdate, canCancel, canRemove]);

  const onRowMenu = useCallback((key, row) => {
    switch (key) {
      case 'view':   return openView(row);
      case 'print':  return openReport(row);
      case 'edit':   return openEdit(row);
      case 'cancel': return runCancel(row);
      case 'delete': return runDelete(row);
      default:       return undefined;
    }
  }, [openView, openReport, openEdit, runCancel, runDelete]);

  // ── Columns ──────────────────────────────────────────────────
  const columns = useMemo(() => ([
    {
      title:     'GRN No',
      dataIndex: 'grnNo',
      width:     168,
      render: (_v, row) => {
        const grnNo = pickField(row, 'grn_no', 'grnNo') || '—';
        return (
          <button
            type="button"
            className="grn-link"
            onClick={() => openView(row)}
          >
            {grnNo}
          </button>
        );
      }
    },
    {
      title:     'GRN Date',
      dataIndex: 'grnDate',
      width:     110,
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtDate(pickField(row, 'grn_date', 'grnDate'))}
        </span>
      )
    },
    {
      title:     'PO No',
      dataIndex: 'poNumberSnapshot',
      width:     140,
      render: (_v, row) => {
        const poNo = pickField(row, 'po_number_snapshot', 'poNumberSnapshot');
        const poId = pickField(row, 'po_id', 'poId');
        if (!poNo && !poId) return <span className="grn-muted">—</span>;
        // Never surface the raw UUID — when only the id is known, offer a
        // plain link to open the source PO instead.
        if (!poId) {
          return <span className="grn-mono">{poNo}</span>;
        }
        return (
          <button
            type="button"
            className="grn-link grn-link--secondary"
            onClick={() => openSourcePo(row)}
          >
            {poNo || 'View PO'}
          </button>
        );
      }
    },
    {
      title:     'Supplier',
      dataIndex: 'supplierName',
      render: (_v, row) => {
        const name  = pickField(row, 'supplier_name',  'supplierName')  || '—';
        const gstin = pickField(row, 'supplier_gstin', 'supplierGstin');
        return (
          <div className="grn-supplier">
            <Tooltip title={name}>
              <span className="grn-supplier__name">{name}</span>
            </Tooltip>
            {gstin ? <span className="grn-supplier__gstin">{gstin}</span> : null}
          </div>
        );
      }
    },
    {
      title:     'Supplier Invoice No',
      dataIndex: 'invoiceRef',
      width:     150,
      render: (_v, row) => {
        const ref = pickField(row, 'invoice_ref', 'invoiceRef')
          || pickField(row, 'supplier_invoice_no', 'supplierInvoiceNo');
        if (!ref) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{ref}</span>;
      }
    },
    {
      title:     'Status',
      dataIndex: 'status',
      width:     160,
      render: (v) => {
        if (!v) return <span className="grn-muted">—</span>;
        return (
          <span className="grn-status">
            <span className="grn-sdot" style={{ background: statusDotColor(v) }} />
            <span className="grn-sname">{statusLabel(v)}</span>
          </span>
        );
      }
    },
    {
      title:     'Received By',
      dataIndex: 'receivedBy',
      width:     140,
      render: (_v, row) => {
        const who = readReceivedBy(row);
        if (!who) return <span className="grn-muted">—</span>;
        return (
          <Tooltip title={who}>
            <span className="grn-receiver">{who}</span>
          </Tooltip>
        );
      }
    },
    {
      title:     '',
      key:       '_actions',
      width:     84,
      fixed:     'right',
      align:     'center',
      render: (_v, row) => {
        const items = buildRowMenu(row);
        const id = pickField(row, 'id', 'id');
        return (
          <Space size={2}>
          <Tooltip title="Print receipt">
            <Button
              type="text"
              size="small"
              className="grn-actions-btn"
              icon={<PrinterOutlined />}
              onClick={(e) => { e.stopPropagation(); openReport(row); }}
              aria-label="Print GRN receipt"
            />
          </Tooltip>
          <Dropdown
            menu={{
              items,
              onClick: ({ key, domEvent }) => {
                if (domEvent && domEvent.stopPropagation) domEvent.stopPropagation();
                onRowMenu(key, row);
              }
            }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              className="grn-actions-btn"
              icon={<MoreOutlined />}
              loading={busyId === id}
              onClick={(e) => e.stopPropagation()}
              aria-label="Row actions"
            />
          </Dropdown>
          </Space>
        );
      }
    }
  ]), [openView, openReport, openSourcePo, buildRowMenu, onRowMenu, busyId]);

  // ── Render ───────────────────────────────────────────────────
  if (!canView) {
    return (
      <div className="grn-shell">
        <Alert
          type="warning"
          showIcon
          message="You don't have permission to view goods receipts."
          description="Ask your master account to grant erp.purchase.grns.view."
        />
      </div>
    );
  }

  return (
    <div className="grn-shell">
      {/* Header strip */}
      <div className="grn-headstrip">
        <div className="grn-headstrip__lead">
          <span className="grn-headstrip__icon"><InboxOutlined /></span>
          <div className="grn-headstrip__text">
            <span className="grn-headstrip__title">Goods receipts</span>
            <span className="grn-headstrip__subtitle">
              Your receiving register — every inbound delivery, its
              inspection outcome, and the PO it settles against.
            </span>
          </div>
        </div>
        <Space size={6}>
          <span className="grn-stat-chip">
            <strong>{total}</strong> {total === 1 ? 'receipt' : 'receipts'}
          </span>
        </Space>
      </div>

      {/* Status filter rail — one-click lifecycle filter. Replaces the
          status dropdown; the GRN lifecycle IS the structure here. */}
      <div className="grn-statusrail" role="tablist" aria-label="Filter by status">
        <button
          type="button"
          className={`grn-chip${!status ? ' grn-chip--active' : ''}`}
          aria-pressed={!status}
          onClick={() => onStatus(null)}
        >
          <span className="grn-chip__dot grn-chip__dot--all" />
          All
        </button>
        {GRN_STATUS_OPTIONS.map((o) => {
          const active = status === o.value;
          return (
            <button
              key={o.value}
              type="button"
              className={`grn-chip${active ? ' grn-chip--active' : ''}`}
              aria-pressed={active}
              onClick={() => onStatus(active ? null : o.value)}
              style={active ? { '--chip-accent': statusDotColor(o.value) } : undefined}
            >
              <span
                className="grn-chip__dot"
                style={{ background: statusDotColor(o.value) }}
              />
              {o.label}
            </button>
          );
        })}
      </div>

      {/* Sticky toolbar */}
      <div className="grn-toolbar">
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search GRN no, PO no, supplier, invoice ref…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onPressEnter={(e) => {
            // Flush debounce on Enter.
            setSearchInput(e.target.value);
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ width: 300 }}
        />
        <RangePicker
          size="small"
          value={dateRange}
          onChange={onDates}
          format={DATE_FMT}
          allowClear
          placeholder={['From', 'To']}
          style={{ width: 240 }}
        />
        <Tooltip title="Clear filters">
          <Button size="small" onClick={onReset}>Clear</Button>
        </Tooltip>
        <div className="grn-toolbar__spacer" />
        <Tooltip title="Refresh">
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={load}
          />
        </Tooltip>
        <Tooltip
          title={canCreate
            ? 'Seed a GRN from an existing purchase order'
            : 'You do not have permission to create GRNs'}
        >
          <span>
            <Button
              size="small"
              icon={<ImportOutlined />}
              onClick={openFromPoModal}
              disabled={!canCreate}
            >
              Create from PO
            </Button>
          </span>
        </Tooltip>
        <Tooltip
          title={canCreate
            ? 'Create a new goods receipt'
            : 'You do not have permission to create GRNs'}
        >
          <span>
            <Button
              size="small"
              type="primary"
              icon={<PlusOutlined />}
              onClick={openNew}
              disabled={!canCreate}
            >
              New GRN
            </Button>
          </span>
        </Tooltip>
      </div>

      {/* Grid card */}
      <Card className="grn-card" bordered={false} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table
            className="grn-grid"
            size="small"
            rowKey={(r) => pickField(r, 'id', 'id') || pickField(r, 'grn_no', 'grnNo')}
            dataSource={rows}
            columns={columns}
            sticky
            scroll={{ x: 1160 }}
            rowClassName={(row) => {
              const st = String(pickField(row, 'status', 'status') || '').toUpperCase();
              return st ? `grn-row grn-row--${st.toLowerCase()}` : 'grn-row';
            }}
            onRow={(row) => ({
              onDoubleClick: () => openView(row)
            })}
            pagination={{
              current:           page,
              pageSize,
              total,
              showSizeChanger:   true,
              pageSizeOptions:   PAGE_SIZE_OPTIONS,
              showQuickJumper:   total > pageSize,
              size:              'small',
              showTotal: (count, range) =>
                count === 0
                  ? 'No rows'
                  : `${range[0]}–${range[1]} of ${count}`,
              onChange: (nextPage, nextSize) => {
                if (nextSize !== pageSize) {
                  setPageSize(nextSize);
                  setPage(1);
                } else {
                  setPage(nextPage);
                }
              }
            }}
            locale={{
              emptyText: loading
                ? ' '
                : 'No goods receipts yet — click New GRN or Create from PO to start.'
            }}
          />
        </Spin>
      </Card>

      {/* "Create from PO" modal */}
      <Modal
        title="Create GRN from purchase order"
        open={fromPoOpen}
        onCancel={closeFromPoModal}
        onOk={submitFromPo}
        okText="Continue"
        cancelText="Cancel"
        destroyOnClose
        width={420}
        centered
      >
        <Descriptions
          size="small"
          column={1}
          colon={false}
          contentStyle={{ fontSize: 12 }}
          labelStyle={{ fontSize: 12, color: '#64748b' }}
          style={{ marginBottom: 10 }}
        >
          <Descriptions.Item label="What this does">
            Seeds a draft GRN from the chosen PO. Quantities default to
            the pending balance — adjust on the next screen before
            saving.
          </Descriptions.Item>
        </Descriptions>
        <Form
          form={fromPoForm}
          layout="vertical"
          size="small"
          requiredMark={false}
          onFinish={submitFromPo}
        >
          <Form.Item
            label="PO No or PO id"
            name="poRef"
            rules={[
              { required: true, message: 'Enter the PO No or PO id.' },
              {
                validator: (_r, v) => {
                  if (v === undefined || v === null) return Promise.resolve();
                  const s = String(v).trim();
                  if (!s) return Promise.resolve();
                  if (s.length > 64) {
                    return Promise.reject(new Error('Value is too long.'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
            extra="Tip: PO Nos are issued by the purchase module (e.g. PO-2025-0042). UUIDs are accepted too."
          >
            <Input
              placeholder="PO-YYYY-NNNN or UUID"
              autoFocus
              allowClear
              onPressEnter={submitFromPo}
            />
          </Form.Item>
          {/* InputNumber is imported per the brief and kept available
              for the editor's quantity drawers; we don't render one in
              this modal because the PO reference is a string, not a
              number. */}
          <div style={{ display: 'none' }}>
            <InputNumber aria-hidden="true" />
          </div>
        </Form>
      </Modal>
    </div>
  );
}
